import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['form', 'emailInput', 'submitButton', 'messages']
  static values = {
    submittingText: String,
    successMessage: String,
    buttonText: String,
    successClass: String,
    errorClass: String,
    validationError: String,
    unexpectedError: String,
    connectionError: String
  }

  declare readonly formTarget: HTMLFormElement
  declare readonly emailInputTarget: HTMLInputElement
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly messagesTarget: HTMLElement
  declare readonly submittingTextValue: string
  declare readonly successMessageValue: string
  declare buttonTextValue: string
  declare readonly successClassValue: string
  declare readonly errorClassValue: string
  declare readonly validationErrorValue: string
  declare readonly unexpectedErrorValue: string
  declare readonly connectionErrorValue: string

  private isSubmitting = false

  connect() {
    this.handleSubmitStart = this.handleSubmitStart.bind(this)
    this.handleSubmitEnd = this.handleSubmitEnd.bind(this)
    this.element.addEventListener('turbo:submit-start', this.handleSubmitStart as EventListener)
    this.element.addEventListener('turbo:submit-end', this.handleSubmitEnd as EventListener)
    
    if (!this.buttonTextValue && this.submitButtonTarget) {
      this.buttonTextValue = this.submitButtonTarget.textContent?.trim() || ''
    }
  }

  disconnect() {
    this.element.removeEventListener('turbo:submit-start', this.handleSubmitStart as EventListener)
    this.element.removeEventListener('turbo:submit-end', this.handleSubmitEnd as EventListener)
  }

  handleSubmitStart(event: Event) {
    if (this.isSubmitting) {
      event.preventDefault()
      return
    }

    this.isSubmitting = true
    if (this.submitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = this.submittingTextValue || 'Enviando...'
    }
    this.clearMessages()
  }

  async handleSubmitEnd(event: Event) {
    this.isSubmitting = false
    
    // @ts-ignore
    const detail = (event as CustomEvent).detail || {}
    const success = Boolean(detail.success)
    const fetchResponse = detail.fetchResponse

    if (success) {
      // Re-enable handled in success timeout or immediately if we don't hide
      const json = await this.extractJson(fetchResponse)
      const msg = json?.message || this.successMessageValue || '¡Gracias! Te hemos agregado a la lista de espera.'
      this.showMessage(String(msg), 'success')
      
      this.formTarget.reset()
      
      // Dispatch custom event so parent controllers can react (e.g. collapse)
      this.dispatch('success')
    } else {
      if (this.submitButtonTarget) {
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.textContent = this.buttonTextValue
      }

      const json = await this.extractJson(fetchResponse)
      let errorMsg = this.unexpectedErrorValue || 'Error inesperado. Inténtalo de nuevo.'
      
      if (json) {
        errorMsg = json.error || (Array.isArray(json.errors) ? json.errors.join(', ') : json.errors) || errorMsg
      } else if (fetchResponse && fetchResponse.response) {
        if (fetchResponse.response.status === 422) {
          errorMsg = this.validationErrorValue || 'Error de validación. Revisa los campos.'
        }
      } else {
        errorMsg = this.connectionErrorValue || 'Error de conexión. Inténtalo de nuevo.'
      }
      
      this.showMessage(errorMsg, 'error')
    }
  }

  private async extractJson(fetchResponse: any) {
    try {
      const resp = fetchResponse && (fetchResponse.response || fetchResponse)
      if (resp && typeof resp.json === 'function') {
        return await resp.json()
      }
    } catch (e) {
      // ignore
    }
    return null
  }

  private showMessage(message: string, type: 'success' | 'error') {
    if (this.messagesTarget) {
      const className = type === 'success' 
        ? (this.successClassValue || 'text-green-400') 
        : (this.errorClassValue || 'text-red-400')
      this.messagesTarget.innerHTML = `<p class="${className}">${message}</p>`
    }
  }

  private clearMessages() {
    if (this.messagesTarget) {
      this.messagesTarget.innerHTML = ''
    }
  }
}
